import { isStoryBackgroundMusicClip } from '@/lib/videoEditorPlayback';

export const SCENE_SFX_SOURCE = 'scene_sfx';
export const ENGINE_SFX_STEM_SOURCE = 'engine_sfx_stem';
export const ENGINE_SFX_STEM_ID = 'engine-sfx-stem';

export const isSceneSfxClip = (clip) => clip?.sourceType === SCENE_SFX_SOURCE || String(clip?.id || '').startsWith('scene-sfx-');
export const isEngineSfxStemClip = (clip) => clip?.sourceType === ENGINE_SFX_STEM_SOURCE || String(clip?.id || '') === ENGINE_SFX_STEM_ID;

export const isSceneSfxLane = (track) => Array.isArray(track) && track.length > 0 && track.every(isSceneSfxClip);
export const isEngineSfxStemLane = (track) => Array.isArray(track) && track.length > 0 && track.every(isEngineSfxStemClip);

/** Sound FX and background music share the musicTracks array but toggle independently. */
export function isMusicLanePlaybackEnabled(timelineActive, track) {
  if (isSceneSfxLane(track) || isEngineSfxStemLane(track)) return timelineActive?.sfx !== false;
  return timelineActive?.music !== false;
}

export function maskMusicTracksForPlayback(tracks, timelineActive) {
  return (tracks || []).map((track) => (isMusicLanePlaybackEnabled(timelineActive, track) ? track : []));
}

const hasPlayableUrl = (clip) => Boolean(clip?.url && String(clip.url).trim());

/**
 * True when the music lanes hold no background music of their own — only scene sound effects or nothing —
 * so the story's music track (e.g. the generated score) should be laid in.
 */
export function musicTracksNeedStoryMusic(tracks) {
  const flat = (tracks || []).flat().filter(Boolean);
  if (flat.some(isStoryBackgroundMusicClip)) return false;
  return !flat.some((clip) => !isSceneSfxClip(clip) && !isEngineSfxStemClip(clip) && hasPlayableUrl(clip));
}

/** Story music lane first, then the lanes that still hold scene SFX (unreadable leftovers are dropped). */
export function withStoryMusicLane(tracks, storyMusicTracks) {
  const musicLane = (storyMusicTracks || [[]])[0] || [];
  const rest = (tracks || [])
    .map((track) => (Array.isArray(track) ? track.filter((clip) => isSceneSfxClip(clip) || hasPlayableUrl(clip)) : []))
    .filter((track) => track.length);
  return [musicLane, ...rest];
}

/** sceneId → { start, end } on the timeline, spanning every video clip cut from that scene. */
export function sceneSlotsFromClips(videoClips) {
  const slots = new Map();
  (videoClips || []).forEach((clip) => {
    const sid = clip?.sceneId != null ? String(clip.sceneId) : (clip?.id != null ? String(clip.id) : '');
    if (!sid) return;
    const start = Number(clip.startTime) || 0;
    const end = start + (Number(clip.duration) || 0);
    const prev = slots.get(sid);
    slots.set(sid, prev ? { start: Math.min(prev.start, start), end: Math.max(prev.end, end) } : { start, end });
  });
  return slots;
}

/** A scene's sound effect plays only while its scene is on screen (generated files often run ~30 s). */
export function sceneSfxSeconds(sfxSeconds, slot) {
  const sfx = Number(sfxSeconds) || 0;
  const span = slot ? slot.end - slot.start : 0;
  if (span > 0 && sfx > 0) return Math.min(sfx, span);
  return sfx > 0 ? sfx : span;
}

/**
 * Scene sound effects live on their own "Sound FX" lane under the music. Existing SFX clips keep the
 * user's volume/mute but follow the scene (url, position, length); SFX found on other lanes move over.
 */
export function placeSceneSfxOnOwnLane(tracks, sfxClips) {
  const lanes = JSON.parse(JSON.stringify(tracks && tracks.length ? tracks : [[]])).map((t) => (Array.isArray(t) ? t : []));
  if (!sfxClips?.length && !lanes.flat().some(isSceneSfxClip)) return lanes;

  const previous = new Map();
  const cleaned = lanes.map((track) => track.filter((clip) => {
    if (!isSceneSfxClip(clip)) return true;
    previous.set(String(clip.id), clip);
    return false;
  }));

  const fromScenes = (sfxClips || []).map((clip) => {
    const prev = previous.get(String(clip.id));
    previous.delete(String(clip.id));
    return prev
      ? {
        ...prev,
        url: clip.url || prev.url,
        startTime: clip.startTime,
        duration: clip.duration,
        sourceDuration: clip.sourceDuration,
        trimStart: 0,
        sceneId: clip.sceneId,
        sourceType: SCENE_SFX_SOURCE,
      }
      : clip;
  });
  const sfxLane = [...fromScenes, ...previous.values()].sort(
    (a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0),
  );

  const kept = cleaned.filter((track, idx) => idx === 0 || track.length || !lanes[idx].length);
  return sfxLane.length ? [...kept, sfxLane] : kept;
}
